import { WorkPipelineStage } from "./work-pipeline";

/**
 * A brand + shopper country taken from Coupon Performance onto the Work
 * Pipeline. Three people can work it — code, APK, dev — and it completes
 * itself once every step that applies is done.
 */
export type TCouponPerformanceReviewStatus =
  | "claimed"
  | "in-pipeline"
  /** Legacy, from before steps existed: reads as code review done. */
  | "pipeline-done"
  | "completed";

export type TCouponReviewStep = "code" | "apk" | "dev";

export type TCouponReviewStepResult =
  "done" | "passed" | "failed" | "fixed" | "disabled" | "not-needed";

/** How a developer closes a Dev fixes row. */
export type TCouponReviewDevResolution = "fixed" | "disabled";

export interface ICouponReviewStepRecord {
  result: TCouponReviewStepResult | null;
  byId: string | null;
  byName: string | null;
  at: string | null;
  /** e.g. "APK broken" on a failed APK test. */
  detail: string | null;
}

export type ICouponReviewSteps = Record<
  TCouponReviewStep,
  ICouponReviewStepRecord
>;

/** Where each step's work happens on the board. */
export const COUPON_REVIEW_STEP_STAGE: Record<
  TCouponReviewStep,
  WorkPipelineStage
> = {
  code: WorkPipelineStage.CouponReview,
  apk: WorkPipelineStage.ApkTesting,
  dev: WorkPipelineStage.DevFixes,
};

export const COUPON_REVIEW_STEP_LABELS: Record<TCouponReviewStep, string> = {
  code: "Code",
  apk: "APK",
  dev: "Dev",
};

/** The step still to do, or null once the review is finished. Dev only follows a failed APK test. */
export const currentCouponReviewStep = (
  steps: Record<TCouponReviewStep, { result: TCouponReviewStepResult | null }>
): TCouponReviewStep | null => {
  if (!steps.code.result) return "code";
  if (!steps.apk.result) return "apk";
  if (steps.apk.result !== "failed") return null;
  if (!steps.dev.result) return "dev";
  // A fix goes back to the APK tester to retest; a disabled one is finished.
  return steps.dev.result === "disabled" ? null : "apk";
};

/** The failed attempt that started the review, as shown on Raw events. */
export interface ICouponPerformanceReviewSource {
  eventId: string | null;
  code: string | null;
  status: string | null;
  message: string | null;
  /** Kept so the domain can be resolved again from the review alone. */
  campaignId: string | null;
  url: string | null;
}

/** The review's row on the board as it is right now. */
export interface ICouponReviewPipelineState {
  stage: WorkPipelineStage;
  isActive: boolean;
  archived: boolean;
  qaListId: string;
  assignedTo: string | null;
  assignedToName: string | null;
  releaseNote: string | null;
  releaseNoteByName: string | null;
  releaseNoteAt: string | null;
  devEscalatedAt: string | null;
  devClaimedByName: string | null;
  devFeedbackAt: string | null;
  devStatus: string | null;
}

export interface ICouponPerformanceReview {
  _id: string;
  brandId: string;
  brandName: string | null;
  countryCode: string;
  domain: string | null;
  status: Exclude<TCouponPerformanceReviewStatus, "pipeline-done">;
  /** Who raised it from Coupon Performance. */
  claimedBy: string;
  claimedByName: string;
  claimedAt: string;
  pipelineItemId: string | null;
  addedToPipelineAt: string | null;
  completedAt: string | null;
  steps: ICouponReviewSteps;
  /** Null until it is on the board. */
  pipeline: ICouponReviewPipelineState | null;
  source: ICouponPerformanceReviewSource;
}

/** What the server worked out for the "Add to pipeline" form. */
export interface ICouponPerformanceReviewDraft {
  brandName: string | null;
  domain: string | null;
  domainSource: string | null;
  /** Why no single domain was found, when none was. */
  domainNote: string | null;
}

export interface ICouponPerformanceReviewKey {
  brandId: string;
  countryCode: string;
}

export interface ICouponPerformanceReviewAddInput extends ICouponPerformanceReviewKey {
  brandName: string;
  domain: string;
  source: ICouponPerformanceReviewSource;
}

/** Reviews completed longer ago than this stop showing, so the row can be reviewed again. */
export const COUPON_PERFORMANCE_REVIEW_SHOWN_DAYS = 30;

export const COUPON_PERFORMANCE_LIST_NAME = "Coupon performance QA";

/** A claim never added to the pipeline can be taken over by anyone after this long. */
export const COUPON_PERFORMANCE_CLAIM_STALE_MS = 24 * 60 * 60 * 1000;
